/**
 * WP-06C-B — The product view of hosted Loop authority.
 *
 * Every value here is DERIVED from what the hosted authority returned. Nothing
 * in this module invents, predicts, or remembers Loop state:
 *
 *   * `status`, `recordVersion`, and every timestamp are copied verbatim from
 *     the authority projection — never computed, never optimistically advanced;
 *   * `nextAction` and the fallback `whyNow` sentence are pure presentation
 *     functions of the authoritative status, so they cannot disagree with it;
 *   * fields the hosted read path does not yet carry (dependencies, evidence
 *     records, activity timeline, conversation linkage, tags) are returned EMPTY
 *     rather than filled from a browser cache. An empty list here means "the
 *     backend has no read path for this yet", never "the browser lost it".
 *
 * The resulting `Loop` object is a temporary view of authority. It is never
 * persisted, never merged with local data, and never used as an input to a
 * mutation other than through the authoritative `recordVersion` it carries.
 */

import type {
  HostedLoopCondition,
  HostedLoopDetail,
  HostedLoopSummary,
} from "../read/loopProjection";
import type { Loop, LoopAction, ResolutionCondition } from "@/lib/looseend/types";

/** The status → next-action mapping. A pure function of authoritative status. */
export function deriveNextAction(
  status: Loop["status"],
  expectedEvent: string,
  triggerCondition: string,
): LoopAction {
  const detail = triggerCondition.trim().length > 0 ? triggerCondition : expectedEvent;
  switch (status) {
    case "open":
      return { kind: "make_decision", label: "Decide the next move", detail };
    case "waiting":
      return { kind: "wait", label: "Nothing to do yet", detail };
    case "action_needed":
      return { kind: "send_reminder", label: "Follow up now", detail };
    case "blocked":
      return { kind: "wait", label: "Unblock this first", detail };
    case "ready_to_verify":
      return { kind: "verify_result", label: "Confirm the result", detail };
    case "resolved":
      return { kind: "verify_result", label: "Resolved and verified", detail };
    case "archived":
      return { kind: "wait", label: "Archived", detail };
  }
}

/** A plain-language reason, used only when the authority carries no `why_now`. */
export function deriveWhyNow(status: Loop["status"], expectedDate: string | null): string {
  const overdue = expectedDate !== null && Date.parse(expectedDate) < Date.now();
  switch (status) {
    case "action_needed":
      return overdue
        ? "The response you expected has not arrived."
        : "This one needs something from you.";
    case "waiting":
      return overdue
        ? "The expected date has passed with no response."
        : "Nothing yet — the other party still has time.";
    case "blocked":
      return "It cannot move until something else resolves first.";
    case "ready_to_verify":
      return "Everything is done except confirming the result.";
    case "resolved":
      return "Resolution was verified.";
    case "archived":
      return "Archived, kept for the record.";
    case "open":
      return "Recently captured and not yet moving.";
  }
}

/**
 * Fold the hosted resolution conditions into the single statement the current UI
 * renders. `verified` is true only when every REQUIRED condition is verified by
 * the backend; an empty condition set is never treated as verified.
 */
export function resolutionConditionView(
  conditions: readonly HostedLoopCondition[],
  fallbackStatement: string,
): ResolutionCondition {
  const required = conditions.filter((c) => c.requirement === "required");
  const considered = required.length > 0 ? required : conditions;
  const statement = considered[0]?.statement ?? fallbackStatement;
  const verified =
    considered.length > 0 && considered.every((c) => c.verificationState === "verified");
  const verifiedAt =
    considered
      .map((c) => c.verifiedAt)
      .filter((at): at is string => typeof at === "string")
      .sort()
      .at(-1) ?? null;
  return { statement, verified, verifiedAt: verified ? verifiedAt : null };
}

const EMPTY_VIEW = {
  dependencies: [] as Loop["dependencies"],
  evidence: [] as Loop["evidence"],
  timeline: [] as Loop["timeline"],
  tags: [] as string[],
  conversationId: null,
} as const;

/**
 * The list-level view. `summary` falls back to the expected event because the
 * list projection deliberately does not fetch long-form text.
 */
export function hostedSummaryToLoopView(summary: HostedLoopSummary): Loop {
  return {
    id: summary.loopId,
    recordVersion: summary.recordVersion,
    resolutionCycle: summary.resolutionCycle,
    title: summary.title,
    summary: summary.expectedEvent,
    objective: summary.expectedEvent,
    status: summary.status,
    ...(summary.counterparty ? { counterparty: summary.counterparty } : {}),
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
    expectedDate: summary.expectedDate,
    expectedEvent: summary.expectedEvent,
    nextAction: deriveNextAction(summary.status, summary.expectedEvent, ""),
    triggerCondition: "",
    resolutionCondition: { statement: summary.expectedEvent, verified: false },
    whyNow: deriveWhyNow(summary.status, summary.expectedDate),
    weight: summary.weight,
    cluster: summary.cluster ?? "Personal",
    archivedAt: summary.archivedAt,
    resolvedAt: summary.resolvedAt,
    snoozedUntil: summary.snoozedUntil,
    ...EMPTY_VIEW,
  };
}

/** The detail-level view, including the authoritative resolution conditions. */
export function hostedDetailToLoopView(detail: HostedLoopDetail): Loop {
  return {
    ...hostedSummaryToLoopView(detail),
    summary: detail.summary,
    objective: detail.objective,
    triggerCondition: detail.triggerCondition,
    nextAction: deriveNextAction(detail.status, detail.expectedEvent, detail.triggerCondition),
    resolutionCondition: resolutionConditionView(detail.conditions, detail.expectedEvent),
    whyNow:
      detail.whyNow.trim().length > 0
        ? detail.whyNow
        : deriveWhyNow(detail.status, detail.expectedDate),
  };
}
